import { useState } from "react";
import Seo from "@/components/Seo";
import { usePublicData } from "@/lib/publicData";
import { PageHero, Container } from "@/components/public/parts";
import { Field, Success, useSubmit, api, Input, Textarea, Button, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Loader2 } from "./formkit";
import { Upload } from "lucide-react";

export default function Referral() {
  const data = usePublicData();
  const services = (data?.services || []).map((s) => s.name);
  const [form, setForm] = useState({ urgency: "MEDIUM" });
  const [file, setFile] = useState(null);
  const { loading, result, submit } = useSubmit();
  const set = (k, v) => setForm({ ...form, [k]: v });

  const onSubmit = (e) => {
    e.preventDefault();
    const fd = new FormData();
    fd.append("referrer_name", form.referrer_name || "");
    fd.append("referrer_org", form.referrer_org || "");
    fd.append("referrer_email", form.referrer_email || "");
    fd.append("referrer_phone", form.referrer_phone || "");
    fd.append("client_name", form.client_name || "");
    fd.append("client_phone", form.client_phone || "");
    fd.append("service", form.service || "");
    fd.append("location", form.location || "");
    fd.append("urgency", form.urgency || "MEDIUM");
    fd.append("message", form.message || "");
    if (file) fd.append("file", file);
    submit(async () => (await api.post("/public/referral", fd, { headers: { "Content-Type": "multipart/form-data" } })).data);
  };

  return (
    <>
      <Seo title="Make a Referral | Love For Mankind" description="Refer an individual to Love For Mankind for compassionate disability and community support services in Ohio." path="/referral" />
      <PageHero eyebrow="For Partners & Case Managers" title="Make a Referral" sub="Refer an individual to Love For Mankind. Attach supporting documents if available." breadcrumbs={[{ label: "Home", to: "/" }, { label: "Referral" }]} />
      <Container className="py-16 lg:py-20 max-w-2xl">
        {result ? <Success refNo={result.reference_no} title="Referral received" message={result.message} /> : (
          <form onSubmit={onSubmit} className="space-y-6 rounded-2xl border border-border bg-card p-7 lg:p-9" data-testid="referral-form">
            <div>
              <p className="font-heading font-semibold mb-3 text-gold uppercase text-xs tracking-widest">Referrer</p>
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Your Name" required><Input required value={form.referrer_name || ""} onChange={(e) => set("referrer_name", e.target.value)} data-testid="rf-referrer" /></Field>
                <Field label="Organization"><Input value={form.referrer_org || ""} onChange={(e) => set("referrer_org", e.target.value)} data-testid="rf-org" /></Field>
                <Field label="Email"><Input type="email" value={form.referrer_email || ""} onChange={(e) => set("referrer_email", e.target.value)} data-testid="rf-email" /></Field>
                <Field label="Phone"><Input value={form.referrer_phone || ""} onChange={(e) => set("referrer_phone", e.target.value)} data-testid="rf-phone" /></Field>
              </div>
            </div>
            <div>
              <p className="font-heading font-semibold mb-3 text-gold uppercase text-xs tracking-widest">Client</p>
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Client Name" required><Input required value={form.client_name || ""} onChange={(e) => set("client_name", e.target.value)} data-testid="rf-client" /></Field>
                <Field label="Client Phone"><Input value={form.client_phone || ""} onChange={(e) => set("client_phone", e.target.value)} data-testid="rf-client-phone" /></Field>
                <Field label="Service">
                  <Select value={form.service || ""} onValueChange={(v) => set("service", v)}>
                    <SelectTrigger data-testid="rf-service"><SelectValue placeholder="Select a service" /></SelectTrigger>
                    <SelectContent>{services.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Location"><Input value={form.location || ""} onChange={(e) => set("location", e.target.value)} data-testid="rf-location" /></Field>
                <Field label="Urgency">
                  <Select value={form.urgency} onValueChange={(v) => set("urgency", v)}>
                    <SelectTrigger data-testid="rf-urgency"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="LOW">Low</SelectItem><SelectItem value="MEDIUM">Medium</SelectItem><SelectItem value="HIGH">High</SelectItem></SelectContent>
                  </Select>
                </Field>
              </div>
            </div>
            <Field label="Message"><Textarea value={form.message || ""} onChange={(e) => set("message", e.target.value)} data-testid="rf-message" /></Field>
            <Field label="Supporting Document (optional)">
              <label className="flex items-center gap-3 rounded-lg border-2 border-dashed border-border px-4 py-3 cursor-pointer hover:border-primary transition-colors" data-testid="rf-file-label">
                <Upload className="w-5 h-5 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">{file ? file.name : "Click to attach a PDF, image or document"}</span>
                <input type="file" className="hidden" accept="image/*,application/pdf,.doc,.docx" onChange={(e) => setFile(e.target.files?.[0])} data-testid="rf-file" />
              </label>
            </Field>
            <Button type="submit" className="rounded-full w-full h-13 min-h-[52px]" disabled={loading} data-testid="rf-submit">{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit Referral"}</Button>
          </form>
        )}
      </Container>
    </>
  );
}
